<template>
  <div class="logs-list">
    <template v-if="isLoadedLogsTypes">
      <div class="logs-list__filters-wrp">
        <div class="app-list-filters">
          <input-field
            class="app-list-filters__field"
            v-model.trim="filters.userId"
            :label="'logs-list.lbl-user-id' | globalize"
          />
          <select-field
            class="app-list-filters__field"
            :label="'logs-list.lbl-type' | globalize"
            v-model="filters.type"
          >
            <option
              v-for="(item, id) in logsTypes"
              :key="`${item}-${id}`"
              :value="item"
            >
              {{ item }}
            </option>
          </select-field>
          <date-field
            class="app-list-filters__field"
            v-model="filters.startDate"
            :label="'logs-list.lbl-start-date' | globalize"
            :enable-time="false"
          />
          <date-field
            class="app-list-filters__field"
            v-model="filters.endDate"
            :label="'logs-list.lbl-end-date' | globalize"
            :enable-time="false"
          />
        </div>
      </div>

      <div class="logs-list__list-wrp">
        <div class="app-list">
          <div class="app-list__header">
            <span class="app-list__cell">
              {{ "logs-list.user-id-title" | globalize }}
            </span>
            <span class="app-list__cell app-list__cell--center">
              {{ "logs-list.type-title" | globalize }}
            </span>
            <span class="app-list__cell app-list__cell--right">
              {{ "logs-list.date-title" | globalize }}
            </span>
          </div>
          <template v-if="isLoaded && logsList.length && !isFailed">
            <button
              v-ripple
              :class="`app-list__li`"
              :disabled="readDisabled"
              v-for="item in logsList"
              :key="`log-${item.id}`"
              @click="showLogDetails(item.id)"
            >
              <span
                class="app-list__cell app-list__cell--left"
                :title="item.userId"
              >
                {{ item.userId | cropAddress(10) }}
              </span>

              <span
                class="app-list__cell app-list__cell--center"
                :title="item.logNotesType"
              >
                {{ item.logNotesType }}
              </span>

              <span
                class="app-list__cell app-list__cell--right"
                :title="item.logTime"
              >
                {{ item.logTime | formatDateDMYT }}
              </span>
            </button>
          </template>

          <template v-else>
            <div class="app-list__li-like">
              <template v-if="isLoaded && !logsList.length">
                <p>
                  {{ "logs-list.nothing-here-yet" | globalize }}
                </p>
              </template>

              <template v-else-if="isFailed">
                <p>
                  {{ "admins-list.default-error-message" | globalize }}
                </p>
              </template>

              <template v-else>
                <p>
                  {{ "logs-list.loading-message" | globalize }}
                </p>
              </template>
            </div>
          </template>
        </div>

        <div class="app__more-btn-wrp">
          <collection-loader
            class="app__more-btn-wrp"
            :first-page-loader="getLogs"
            @first-page-load="setList"
            @next-page-load="concatList"
            ref="collectionLoaderBtn"
          />
        </div>
      </div>
    </template>
    <template v-else>
      <loader
        class="logs-list__loader"
        message-id="loading"
        position-center
      />
    </template>
  </div>
</template>

<script>
import SelectField from '@/vue/fields/SelectField'
import DateField from '@/vue/fields/DateField'
import InputField from '@/vue/fields/InputField'
import CollectionLoader from '@/vue/common/CollectionLoader'
import Loader from '@/vue/common/Loader'
import { addParamsToLocation, queryToFilters } from '@/js/helpers/router-query'
import moment from 'moment'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { api } from '@/api'
import { vueRoutes } from '@/vue-router/routes'
import { LogRecord } from '@/js/records/entities/log.record'
import { clearObject } from '@/js/helpers/clear-object'

const defaultFilters = {
  userId: '',
  type: '',
  startDate: '',
  endDate: '',
}

export default {
  name: 'logs-list',
  components: {
    InputField,
    SelectField,
    DateField,
    CollectionLoader,
    Loader,
  },
  mixins: [ PermissionMixin ],
  data () {
    return {
      isLoaded: false,
      isFailed: false,
      isLoadedLogsTypes: false,
      isPending: false,
      logsList: [],
      logsTypes: [],
      filters: defaultFilters,
    }
  },
  computed: {
    readDisabled () {
      const result = this.isForbidden('logs', 'log', 'read_foreign')
      return result
    },
  },
  watch: {
    filters: {
      handler () {
        this.isPending = true
        if (this.isLoaded) {
          this.reloadList()
        }
      },
      deep: true,
    },
    isLoaded: {
      handler (value) {
        if (value && this.isPending) {
          this.reloadList()
          this.isPending = false
        }
      },
    },
    '$route' () {
      this.filters = queryToFilters(defaultFilters)
      return this.getLogs()
    },
  },
  async created () {
    this.filters = queryToFilters(defaultFilters)
    await this.getLogs()
    await this.getLogsTypes()
  },
  methods: {
    async getLogsTypes () {
      this.isLoadedLogsTypes = false
      try {
        const { data } = await api.getWithSignature('/logs/types')
        this.logsTypes = data.logTypes
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
      this.isLoadedLogsTypes = true
    },

    async getLogs () {
      this.isLoaded = false
      this.isFailed = false

      let response = {}
      try {
        const query = {
          filter: clearObject({
            user_id: this.filters.userId,
            type: this.filters.type,
            after: this.filters.startDate
              ? moment(this.filters.startDate).format()
              : '',
            before: this.filters.endDate
              ? moment(this.filters.endDate).format()
              : '',
          }),
        }

        response = await api.getWithSignature('/logs', query)
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        this.isFailed = true
      }

      this.isLoaded = true
      return response
    },

    setList (data) {
      this.logsList = data.map(i => new LogRecord(i))
    },

    concatList (data) {
      this.logsList = this.logsList.concat(data.map(i => new LogRecord(i)))
    },

    showLogDetails (id) {
      if (id) {
        this.$router.push({
          name: vueRoutes.logDetails.name,
          params: {
            id: id,
          },
        })
      }
    },

    reloadList () {
      if (this.$refs.collectionLoaderBtn) {
        addParamsToLocation(this.$route, this.filters)
        this.$refs.collectionLoaderBtn.loadFirstPage()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@scss/variables';

.logs-list__list-wrp {
  margin-top: 4rem;
}

</style>
